import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";

import Header from "../components/Header";
import LanguageSet from "../components/language/LanguageSet";
import LanguageContext from "../components/hooks/LanguageContext";
import LoadingSnipQuiz from "../components/LoadingSnipQuiz";
import Constants from "../common/Constants";
import { useAuth } from "../providers/AuthProvider";
import { getText } from "../utils/languageUtils";
import ConsentForm from "./baseline/ConsentForm";

/**
 * Consent on its own, for students with no active quiz.
 *
 * Consent used to be only a stage inside BaseLineQuiz/SurveyQuiz, so when both
 * were deactivated for an age group the student skipped past those pages - and
 * past consent with them - straight to the course list. This keeps consent a
 * gate in its own right; the quiz pages still handle it when one is active.
 */
const ConsentGate = () => {
    const { user, authToken, logout: authLogout, updateUser } = useAuth();
    const navigate = useNavigate();
    const { language, translations: translationData } = useContext(LanguageContext);

    const [loading, setLoading] = useState(true);
    const [consentFormData, setConsentFormData] = useState("");
    const [dataConsent, setDataConsent] = useState(false);

    useEffect(() => {
        const fetchConsent = async () => {
            try {
                setLoading(true);
                const response = await axios.get(
                    `${Constants.BASE_URL}/consent-detail?access_token=${authToken}`
                );

                if (response?.data?.message === "Invalid Access Token") {
                    authLogout();
                    navigate("/");
                    return;
                }

                setConsentFormData(response?.data?.consent_summary);
            } catch (error) {
                console.error(error);
                toast.warn("An error occurred. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchConsent();
    }, [authToken]);

    const submitConsentForm = async () => {
        if (!dataConsent) {
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();
            formData.append("access_token", authToken);
            formData.append("consent", 1);

            const response = await axios.post(
                `${Constants.BASE_URL}/student-consent`,
                formData,
                {
                    headers: {
                        "x-api-key": "fc9f4a47-4f7f-41ba-a656-d30e0009d6f0",
                        "Access-Control-Allow-Origin": "*",
                    },
                }
            );

            if (response?.data?.message === "Invalid Access Token") {
                authLogout();
                navigate("/");
                return;
            }

            // App.js re-routes on its own once consent is recorded: with no quiz
            // to gate on, the student lands on the course list.
            updateUser({ is_consent: 1 });
        } catch (error) {
            console.error(error);
            toast.warn("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ToastContainer />
            {loading && <LoadingSnipQuiz />}
            <div id="body_bg">
                {user && (
                    <Header
                        page_title={
                            (getText(user?.student_name, language) || "") + " (" + user?.student_id + ")"
                        }
                        pageTitle="AskAge"
                    />
                )}
                <LanguageSet />
                <ConsentForm
                    consentFormData={consentFormData}
                    dataConsent={dataConsent}
                    setDataConsent={setDataConsent}
                    submitConsentForm={submitConsentForm}
                    language={language}
                    translationData={translationData}
                />
            </div>
        </>
    );
};

export default ConsentGate;
